/**
 * notificationCard — 종 드롭다운 카드와 시스템 알림 토스트가 공유하는 표시/클릭 라우팅 로직.
 * 두 곳의 레이아웃과 동작을 동일하게 유지하기 위해 분리.
 */
import type { AppNotification } from "../contexts/NotificationContext";
import { shouldIgnoreClick } from "./clickGuard";

// board_type → 사용자 페이지 popup key 매핑
export const BOARD_TO_POPUP: Record<string, string> = {
  GENERAL_NOTICE: "notice",
  TRAVEL_INFO: "travelInfo",
  HELP_BOARD: "help",
  SERVICE_CENTER: "serviceCenter",
  AD_INQUIRY: "adInquiry",
  IMPROVEMENT_REQUEST: "improvementRequest",
  BUG_REPORT: "bugReport",
};

export function getNotifAvatar(n: Pick<AppNotification, "source">) {
  const isAdminSrc = (n.source || "").toLowerCase().includes("manager");
  return {
    isAdminSrc,
    avatarLabel: isAdminSrc ? "TPA" : "TP",
    avatarBg: isAdminSrc
      ? "bg-gradient-to-br from-blue-700 to-blue-900 text-white"
      : "bg-gradient-to-br from-amber-500 to-orange-400 text-white",
  };
}

export function getNotifBoardKey(n: Pick<AppNotification, "boardType" | "type">) {
  return n.boardType || n.type || "";
}

export function fmtNotifTime(ts: number) {
  const d = new Date(ts);
  const now = new Date();
  const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (d.toDateString() === now.toDateString()) return hhmm;
  return `${d.getMonth() + 1}/${d.getDate()} ${hhmm}`;
}

/**
 * 알림 카드/토스트 클릭 시 목적지 라우팅 — 종 드롭다운과 토스트가 동일하게 동작하도록 공용화.
 * onOpenPopup 을 안 넘기면(토스트처럼 Header 의 activePopup 에 직접 접근 못 하는 경우) 전역
 * "open-popup" 이벤트로 대체 — Header.tsx 가 이미 이 이벤트를 구독 중이라 그대로 동작한다.
 */
export function routeNotificationClick(
  n: AppNotification,
  onOpenPopup?: (key: string) => void,
) {
  if (shouldIgnoreClick()) return;
  const openPopup = onOpenPopup || ((key: string) => window.dispatchEvent(new CustomEvent("open-popup", { detail: key })));
  const type = getNotifBoardKey(n);

  // 1) 배너 채팅 알림 → 해당 배너 상세 + chat 탭
  if (type === "BANNER_CHAT" && n.targetId) {
    window.dispatchEvent(new CustomEvent("open-banner", { detail: { bannerId: n.targetId, tab: "chat" } }));
    return;
  }
  // 2) 게시판 알림 → 해당 게시판 popup + 상세 항목 자동 오픈 (즉시)
  const popupKey = BOARD_TO_POPUP[type];
  if (popupKey) {
    openPopup(popupKey);
    if (n.targetId) {
      // popup mount 직후 listener 등록되도록 다음 frame 으로만 미세 지연
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent("open-board-item", { detail: { boardType: type, itemId: Number(n.targetId) } }));
      });
    }
    return;
  }
  // 3) targetId 만 있는 알림 → 배너로 추정
  if (n.targetId) {
    window.dispatchEvent(new CustomEvent("open-banner", { detail: { bannerId: n.targetId, tab: "chat" } }));
    return;
  }
  // 4) fallback — 공지사항 popup
  openPopup("notice");
}
